import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { 
  FiArrowLeft, 
  FiPrinter, 
  FiTruck, 
  FiPackage, 
  FiCalendar, 
  FiUser, 
  FiCreditCard, 
  FiMapPin, 
  FiPhone, 
  FiMail, 
  FiRefreshCw, 
  FiCheckCircle, 
  FiClock, 
  FiAlertCircle, 
  FiDollarSign 
} from "react-icons/fi";
import { Badge, Table } from 'react-bootstrap';
import Loader from '../../component/loader';
import TableThumbImage from '../../component/tableThumbImage';
import axios from 'axios';
import Cookies from "js-cookie";
import { decrpt } from "../../component/encryption";
import ReactToPrint from 'react-to-print';
import ComponentToPrint from './componentToPrint';
import ComponentToPrintLabel from './componentToPrintLabel';

function OrderSingle() {
  const componentRef = useRef();
  const printLabelRef = useRef();
  const location = useLocation();
  const navigate = useNavigate();

  const [loader, setLoader] = useState(false);
  const [orderDetails, setOrderDetails] = useState('');
  const [orderDetailsProduct, setOrderDetailsProduct] = useState('');
  const [addresses, setAddresses] = useState([]);
  const [fetchingAddresses, setFetchingAddresses] = useState(false);
  const [selectedPickups, setSelectedPickups] = useState({});
  const [shiprocketShipments, setShiprocketShipments] = useState([]);
  const [loadingShipments, setLoadingShipments] = useState(false);

  useEffect(() => {
    const fetchAddresses = async () => {
      setFetchingAddresses(true);
      try {
        let token = decrpt(Cookies.get('s_l'));
        const res = await axios.get('/seller/shiprocket/pickup-address/list', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data?.status && Array.isArray(res.data.data)) {
          setAddresses(res.data.data);
          if (res.data.data.length > 0) {
            setSelectedPickups({ seller: res.data.data[0].pickup_code });
          }
        }
      } catch {
        // silently fail
      } finally {
        setFetchingAddresses(false);
      }
    };
    fetchAddresses();
  }, []);

  const fetchShiprocketShipments = async (orderId) => {
    if (!orderId) return;
    setLoadingShipments(true);
    try {
      const token = decrpt(Cookies.get('s_l'));
      const res = await axios.get(`/seller/shiprocket/shipment-details/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.status && res.data.data) {
        const payload = res.data.data;
        setShiprocketShipments(Array.isArray(payload) ? payload : [payload]);
      } else {
        setShiprocketShipments([]);
      }
    } catch {
      setShiprocketShipments([]);
    } finally {
      setLoadingShipments(false);
    }
  };

  const handleShiprocket = async () => {
    try {
      if (!orderDetails) {
        alert("Order data is not loaded yet.");
        return;
      }

      const orderUuid = orderDetails.order_uuid || `ORDER-${Date.now()}`;
      const orderDate = orderDetails.order_date || new Date().toISOString().split('T')[0];
      const userName = orderDetails.user_name || "Customer";

      const billing = (orderDetails.billingAddress && orderDetails.billingAddress.address)
        ? orderDetails.billingAddress
        : (orderDetails.shippingAddress || {});

      const billingAddress = billing.address || "No Address Provided";
      const billingCity = billing.city || "Guwahati";
      const billingPin = billing.pin || "781001";
      const billingState = billing.state || "Assam";
      const billingPhone = billing.mobile || "9999999999";

      let totalWeightInGrams = 0;
      let maxLength = 0;
      let maxBreadth = 0;
      let maxHeight = 0;

      if (orderDetailsProduct && orderDetailsProduct.length > 0) {
        orderDetailsProduct.forEach(item => {
          const qty = Number(item.quantity || 1);
          const itemWeight = Number(item.weight || 500);
          totalWeightInGrams += itemWeight * qty;
          maxLength = Math.max(maxLength, Number(item.length || 10));
          maxBreadth = Math.max(maxBreadth, Number(item.breadth || 10));
          maxHeight = Math.max(maxHeight, Number(item.height || 10));
        });
      } else {
        totalWeightInGrams = 500; maxLength = 10; maxBreadth = 10; maxHeight = 10;
      }

      let totalWeight = Math.max(Number((totalWeightInGrams / 1000).toFixed(3)), 0.01) || 0.5;
      if (maxLength <= 0) maxLength = 10;
      if (maxBreadth <= 0) maxBreadth = 10;
      if (maxHeight <= 0) maxHeight = 10;

      let pickupLocationCode = "Primary";
      if (orderDetailsProduct && orderDetailsProduct.length > 0 && orderDetailsProduct[0].pickup_location) {
        pickupLocationCode = orderDetailsProduct[0].pickup_location;
      } else if (addresses.length > 0) {
        pickupLocationCode = addresses[0].pickup_code;
      }

      setLoader(true);

      const vendorId = orderDetails.seller_id || 'seller';

      const mappedItems = orderDetailsProduct && orderDetailsProduct.length > 0 ? orderDetailsProduct.map(p => {
        const item = {
          name: p.product_name,
          sku: `${orderUuid}-${vendorId}`,
          units: p.quantity,
          selling_price: p.price,
          discount: p.mrp ? (p.mrp - p.price) : 0
        };
        if (p.tax) item.tax = p.tax;
        if (p.hsn) item.hsn = p.hsn;
        return item;
      }) : [{
        name: "Product",
        sku: `${orderUuid}-${vendorId}`,
        units: 1,
        selling_price: orderDetails.total_price,
        discount: 0
      }];

      const vendorSubTotal = orderDetailsProduct ? orderDetailsProduct.reduce((sum, item) => sum + Number(item.product_price_with_gst || (item.price * item.quantity)), 0) : orderDetails.total_price;

      const [bFirstName, ...bRest] = userName.split(' ');
      const bLastName = bRest.join(' ') || '';

      const payload = {
        "order_id": `${orderUuid}-${vendorId}`,
        "internal_order_id": orderDetails.id,
        "order_date": orderDate,
        "pickup_location": pickupLocationCode,
        "comment": "Order from Aivimart",
        "billing_customer_name": bFirstName,
        "billing_last_name": bLastName,
        "billing_address": billingAddress,
        "billing_address_2": billing.street || "",
        "billing_city": billingCity,
        "billing_pincode": billingPin,
        "billing_state": billingState,
        "billing_country": billing.country || "India",
        "billing_email": orderDetails.email || "customer@aivimart.com",
        "billing_phone": billingPhone,
        "shipping_is_billing": true,
        "order_items": mappedItems,
        "payment_method": orderDetails.payment_type || "COD",
        "shipping_charges": orderDetails.shipping_charge || 0,
        "giftwrap_charges": orderDetails.giftwrap_charges || 0,
        "transaction_charges": orderDetails.transaction_charges || 0,
        "total_discount": orderDetails.total_discount || 0,
        "sub_total": vendorSubTotal,
        "length": maxLength,
        "breadth": maxBreadth,
        "height": maxHeight,
        "weight": totalWeight
      };

      const token = decrpt(Cookies.get('s_l'));

      const response = await axios.post('/seller/shiprocket/create-order', payload, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      alert('Shiprocket order created successfully!');
      fetchShiprocketShipments(orderDetails.id || orderDetails.unique_id);
    } catch (error) {
      console.error('Shiprocket Error:', error.response?.data || error.message);
      alert('Failed to create Shiprocket order');
    } finally {
      setLoader(false);
    }
  };

  const fetchOrderDitails = async () => {
    setLoader(true);
    const data = {
      order_id: location.state?.item,
    };
    try {
      let token = decrpt(Cookies.get('s_l'));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const response = await axios.post(`/seller/orders/details`, data);
      if (response.data?.success) {
        setLoader(false);
        setOrderDetails(response.data.order_data);
        setOrderDetailsProduct(response.data.order_details);
        if (response.data.order_data && (response.data.order_data.id || response.data.order_data.unique_id)) {
          fetchShiprocketShipments(response.data.order_data.id || response.data.order_data.unique_id);
        }
      } else {
        setLoader(false);
      }
    } catch (error) {
      console.log(error);
      setLoader(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchOrderDitails();
  }, []);

  const getPaymentStatusBadge = (status) => {
    switch (Number(status)) {
      case 2:
        return <span style={{ background: '#dcfce7', color: '#15803d', fontWeight: '600', padding: '4px 10px', borderRadius: '50px', fontSize: '0.78rem' }}>Paid</span>;
      case 1:
        return <span style={{ background: '#fef3c7', color: '#b45309', fontWeight: '600', padding: '4px 10px', borderRadius: '50px', fontSize: '0.78rem' }}>Pending</span>;
      default:
        return <span style={{ background: '#fee2e2', color: '#b91c1c', fontWeight: '600', padding: '4px 10px', borderRadius: '50px', fontSize: '0.78rem' }}>Failed</span>;
    }
  };

  return (
    <div className="main-content" style={{ background: '#f8fafc', minHeight: '100vh', padding: '24px 20px' }}>
      <style>{`
        .order-panel-box {
          background: #ffffff !important;
          border: 1px solid #e2e8f0 !important;
          border-radius: 12px !important;
          box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05), 0 1px 2px -1px rgba(0, 0, 0, 0.05) !important;
          margin-bottom: 24px !important;
          overflow: hidden !important;
        }
        .order-panel-header {
          padding: 16px 22px;
          border-bottom: 1px solid #f1f5f9;
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: #ffffff;
        }
        .order-panel-title {
          font-size: 1.05rem;
          font-weight: 700;
          color: #0f172a;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .modern-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
        }
        .modern-table thead th {
          background-color: #f8fafc !important;
          color: #475569 !important;
          font-weight: 600 !important;
          font-size: 0.8rem !important;
          text-transform: uppercase !important;
          letter-spacing: 0.04em !important;
          padding: 12px 18px !important;
          border-bottom: 1px solid #e2e8f0 !important;
          border-top: none !important;
        }
        .modern-table tbody td {
          padding: 14px 18px !important;
          vertical-align: middle !important;
          border-bottom: 1px solid #f1f5f9 !important;
          font-size: 0.875rem !important;
          color: #334155;
        }
        .modern-table tbody tr:last-child td {
          border-bottom: none !important;
        }
        .modern-table tbody tr:hover td {
          background-color: #fafbfd !important;
        }
      `}</style>

      {loader ? (
        <Loader />
      ) : (
        <div className="container-fluid p-0">
          {/* Top Bar Navigation & Actions */}
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
            <div className="d-flex align-items-center gap-3">
              <button 
                onClick={() => navigate('/order')} 
                className="btn btn-sm"
                style={{ 
                  background: '#ffffff', 
                  border: '1px solid #d1d5db', 
                  color: '#374151', 
                  fontWeight: '500', 
                  borderRadius: '8px', 
                  padding: '7px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <FiArrowLeft size={16} /> Back to Orders
              </button>
              <div>
                <h4 className="fw-bold text-dark mb-0" style={{ fontSize: '1.35rem' }}>
                  Order Details
                </h4>
                <div className="text-muted mt-1" style={{ fontSize: '0.85rem' }}>
                  UUID: <span className="fw-medium text-dark">{orderDetails?.order_uuid || orderDetails?.id || '—'}</span>
                </div>
              </div>
            </div>

            <div className="d-flex align-items-center gap-2">
              <ReactToPrint
                trigger={() => (
                  <button 
                    className="btn btn-sm" 
                    type="button"
                    style={{
                      background: '#1e293b',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '8px 16px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <FiPrinter size={16} /> Print Receipt
                  </button>
                )}
                content={() => componentRef.current}
                pageStyle="@page { size: A4 portrait; margin: 8mm; } @media print { html, body { margin: 0 !important; padding: 0 !important; } }"
              />

              <ReactToPrint
                trigger={() => (
                  <button 
                    className="btn btn-sm" 
                    type="button"
                    style={{
                      background: '#334155',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '8px 16px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <FiPrinter size={16} /> Print Label
                  </button>
                )}
                content={() => printLabelRef.current}
                pageStyle="@page { size: auto; margin: 0; } @media print { html, body { height: 100% !important; margin: 0 !important; padding: 3mm !important; overflow: hidden !important; } }"
              />

              <button 
                className="btn btn-sm" 
                type="button" 
                onClick={handleShiprocket}
                style={{
                  background: '#0284c7',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <FiTruck size={16} /> Create Shiprocket Order
              </button>
            </div>
          </div>

          {/* Quick Metrics & Overview Strip */}
          <div className="order-panel-box p-3 mb-4">
            <div className="row g-3 text-start">
              <div className="col-6 col-md-4 col-lg-2">
                <div className="text-muted" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: '600' }}>Order Date</div>
                <div className="fw-bold text-dark mt-1 d-flex align-items-center gap-1" style={{ fontSize: '0.95rem' }}>
                  <FiCalendar size={15} className="text-primary" /> {orderDetails?.order_date || '—'}
                </div>
              </div>

              <div className="col-6 col-md-4 col-lg-2">
                <div className="text-muted" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: '600' }}>Customer</div>
                <div className="fw-bold text-dark mt-1 text-truncate d-flex align-items-center gap-1" style={{ fontSize: '0.95rem' }} title={orderDetails?.user_name}>
                  <FiUser size={15} className="text-info" /> {orderDetails?.user_name || 'Customer'}
                </div>
              </div>

              <div className="col-6 col-md-4 col-lg-2">
                <div className="text-muted" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: '600' }}>Order Amount</div>
                <div className="fw-bold mt-1" style={{ fontSize: '1.05rem', color: '#16a34a' }}>
                  ₹ {orderDetails ? Number(orderDetails.overall_price ?? orderDetails.order_amount ?? orderDetails.total_price ?? 0).toFixed(2) : '0.00'}
                </div>
              </div>

              <div className="col-6 col-md-4 col-lg-2">
                <div className="text-muted" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: '600' }}>Payment Method</div>
                <div className="mt-1">
                  <span style={{ 
                    background: orderDetails?.payment_type === 'COD' ? '#fef3c7' : '#e0f2fe', 
                    color: orderDetails?.payment_type === 'COD' ? '#92400e' : '#0369a1',
                    padding: '3px 8px',
                    borderRadius: '6px',
                    fontSize: '0.78rem',
                    fontWeight: '600'
                  }}>
                    {orderDetails?.payment_type === 'COD' ? 'Cash On Delivery' : (orderDetails?.payment_type || 'Online')}
                  </span>
                </div>
              </div>

              <div className="col-6 col-md-4 col-lg-2">
                <div className="text-muted" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: '600' }}>Payment Status</div>
                <div className="mt-1">
                  {getPaymentStatusBadge(orderDetails?.payment_status)}
                </div>
              </div>

              <div className="col-6 col-md-4 col-lg-2" style={{ borderLeft: '1px solid #f1f5f9' }}>
                <div className="text-muted" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: '600' }}>Shiprocket Freight</div>
                <div className="fw-bold mt-1" style={{ fontSize: '0.95rem', color: '#d97706' }}>
                  {loadingShipments ? '…' : (
                    shiprocketShipments[0]?.assigned_freight != null ? (
                      `₹ ${Number(shiprocketShipments[0].assigned_freight).toFixed(2)}`
                    ) : (
                      <span className="text-muted fw-normal" style={{ fontSize: '0.82rem' }}>Not assigned</span>
                    )
                  )}
                </div>
                {shiprocketShipments[0]?.assigned_courier_name && (
                  <div className="text-muted text-truncate" style={{ fontSize: '0.75rem' }}>
                    {shiprocketShipments[0].assigned_courier_name}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Addresses Section (Two Column Modern Cards) */}
          <div className="row g-3 mb-4">
            {/* Shipping Address */}
            <div className="col-md-6">
              <div className="order-panel-box h-100 p-0">
                <div className="order-panel-header">
                  <div className="order-panel-title">
                    <FiMapPin className="text-primary" size={18} /> Shipping Address
                  </div>
                  <span>
                    {(orderDetails?.shippingAddress?.address_type === 'O') ?
                      <span style={{ background: '#f1f5f9', color: '#475569', padding: '3px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '600' }}>Office</span> :
                      <span style={{ background: '#f1f5f9', color: '#475569', padding: '3px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '600' }}>Home</span>}
                  </span>
                </div>
                <div className="p-3">
                  <p className="fw-semibold text-dark mb-1" style={{ fontSize: '0.95rem' }}>
                    {orderDetails?.user_name || 'Customer'}
                  </p>
                  <p className="text-muted mb-2" style={{ fontSize: '0.875rem', lineHeight: '1.5' }}>
                    {orderDetails?.shippingAddress ? (
                      `${orderDetails.shippingAddress.address || ''}, ${orderDetails.shippingAddress.street || ''} ${orderDetails.shippingAddress.land_mark || ''} ${orderDetails.shippingAddress.city || ''} - ${orderDetails.shippingAddress.pin || ''}, ${orderDetails.shippingAddress.state || ''}`
                    ) : 'Shipping address not available'}
                  </p>
                  {orderDetails?.shippingAddress?.mobile && (
                    <div className="d-flex align-items-center gap-2 text-dark fw-medium" style={{ fontSize: '0.875rem' }}>
                      <FiPhone size={14} className="text-muted" /> +91 {orderDetails.shippingAddress.mobile}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Seller Address */}
            <div className="col-md-6">
              <div className="order-panel-box h-100 p-0">
                <div className="order-panel-header">
                  <div className="order-panel-title">
                    <FiPackage className="text-success" size={18} /> Seller Pickup Address
                  </div>
                  {addresses && addresses.length > 0 && addresses[0]?.pickup_code && (
                    <span style={{ background: '#f0fdf4', color: '#166534', padding: '3px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '600' }}>
                      {addresses[0].pickup_code}
                    </span>
                  )}
                </div>
                <div className="p-3">
                  {addresses && addresses.length > 0 ? (
                    <>
                      <p className="fw-semibold text-dark mb-1" style={{ fontSize: '0.95rem' }}>
                        {addresses[0].name}
                      </p>
                      <p className="text-muted mb-2" style={{ fontSize: '0.875rem', lineHeight: '1.5' }}>
                        {addresses[0].address}
                        {addresses[0].address_2 ? `, ${addresses[0].address_2}` : ''} {addresses[0].city} - {addresses[0].pin_code}, {addresses[0].state}
                      </p>
                      <div className="d-flex flex-wrap align-items-center gap-3 text-dark fw-medium" style={{ fontSize: '0.875rem' }}>
                        <span className="d-flex align-items-center gap-1"><FiPhone size={14} className="text-muted" /> +91 {addresses[0].phone}</span>
                        {addresses[0].email && (
                          <span className="d-flex align-items-center gap-1"><FiMail size={14} className="text-muted" /> {addresses[0].email}</span>
                        )}
                      </div>
                    </>
                  ) : (
                    <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>Seller address not available</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Ordered Items Table */}
          <div className="order-panel-box mb-4">
            <div className="order-panel-header">
              <div className="order-panel-title">
                <FiPackage size={18} className="text-primary" /> Ordered Items
                <span className="badge bg-light text-dark border ms-1" style={{ fontSize: '0.75rem', fontWeight: '600' }}>
                  {orderDetailsProduct ? orderDetailsProduct.length : 0} {orderDetailsProduct?.length === 1 ? 'item' : 'items'}
                </span>
              </div>
            </div>

            <div className="table-responsive">
              <table className="modern-table">
                <thead>
                  <tr>
                    <th>Item ID</th>
                    <th>Product</th>
                    <th>Brand</th>
                    <th className="text-center">Quantity</th>
                    <th className="text-end">MRP</th>
                    <th className="text-end">Selling Price</th>
                    <th className="text-center">Discount</th>
                  </tr>
                </thead>
                <tbody>
                  {orderDetailsProduct && orderDetailsProduct.length > 0 ? (
                    orderDetailsProduct.map((item, index) => {
                      const discountRate = item && item.mrp && Number(item.mrp) > 0
                        ? Math.max(0, (100 * (Number(item.mrp) - Number(item.price || 0))) / Number(item.mrp)).toFixed(2)
                        : '0.00';

                      return (
                        <tr key={index}>
                          <td className="text-muted" style={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>
                            {item.unique_id || item.id || '—'}
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-3">
                              <div style={{ width: '46px', height: '46px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e2e8f0', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
                                <TableThumbImage image={item.product_image} className="table-img" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              </div>
                              <div>
                                <span className="fw-semibold text-dark d-block" style={{ fontSize: '0.9rem' }}>
                                  {item?.product_name || '—'}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span style={{ background: '#f1f5f9', color: '#475569', padding: '3px 8px', borderRadius: '4px', fontSize: '0.78rem', fontWeight: '500' }}>
                              {item?.brand || '—'}
                            </span>
                          </td>
                          <td className="text-center fw-medium">
                            {item?.quantity || 1}
                          </td>
                          <td className="text-end text-muted" style={{ textDecoration: Number(discountRate) > 0 ? 'line-through' : 'none' }}>
                            ₹ {Number(item?.mrp || 0).toFixed(2)}
                          </td>
                          <td className="text-end fw-bold text-dark">
                            ₹ {Number(item?.price || 0).toFixed(2)}
                          </td>
                          <td className="text-center">
                            {Number(discountRate) > 0 ? (
                              <span style={{ background: '#dcfce7', color: '#15803d', padding: '3px 8px', borderRadius: '6px', fontSize: '0.78rem', fontWeight: '600' }}>
                                {discountRate}% OFF
                              </span>
                            ) : (
                              <span className="text-muted" style={{ fontSize: '0.78rem' }}>0%</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center py-4 text-muted">
                        No product items found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Seller Payout & Commission Breakdown */}
          <div className="order-panel-box mb-4">
            <div className="order-panel-header">
              <div className="order-panel-title">
                <FiDollarSign size={18} className="text-success" /> Seller Payout & Commission Breakdown
              </div>
              <small className="text-muted" style={{ fontSize: '0.8rem' }}>
                Item Price − Platform Category Commission = Seller Payout
              </small>
            </div>

            <div className="table-responsive">
              <table className="modern-table">
                <thead>
                  <tr>
                    <th>Product Details</th>
                    <th className="text-end">Product Price</th>
                    <th className="text-end">Category Commission</th>
                    <th className="text-end fw-bold text-success">Net Seller Payout</th>
                  </tr>
                </thead>
                <tbody>
                  {orderDetailsProduct && orderDetailsProduct.length > 0 ? (
                    orderDetailsProduct.map((item, idx) => {
                      const pPrice = Number(item.product_price || (item.price * item.quantity) || 0);
                      const commRate = Number(item.commission_rate || 0);
                      const commAmount = Number(item.commission_amount || (pPrice * (commRate / 100)) || 0);
                      const sellerAmt = Number(pPrice - commAmount);

                      return (
                        <tr key={idx}>
                          <td>
                            <div className="fw-semibold text-dark" style={{ fontSize: '0.9rem' }}>{item.product_name}</div>
                            <div className="text-muted" style={{ fontSize: '0.78rem', marginTop: '2px' }}>
                              Base: ₹ {pPrice.toFixed(2)} &nbsp;|&nbsp; Commission: {commRate}% (₹ {commAmount.toFixed(2)})
                            </div>
                          </td>
                          <td className="text-end fw-medium">
                            ₹ {pPrice.toFixed(2)}
                          </td>
                          <td className="text-end" style={{ color: '#dc2626', fontWeight: '500' }}>
                            - ₹ {commAmount.toFixed(2)} <span className="text-muted" style={{ fontSize: '0.75rem' }}>({commRate}%)</span>
                          </td>
                          <td className="text-end fw-bold text-success" style={{ fontSize: '0.95rem' }}>
                            ₹ {sellerAmt.toFixed(2)}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={4} className="text-center py-4 text-muted">
                        No breakdown data available.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Financial Summaries Side-by-Side */}
            <div className="p-3 border-top" style={{ background: '#fafbfd' }}>
              <div className="row g-3 justify-content-between align-items-start">
                {/* Customer Invoice Totals with Platform Fee */}
                <div className="col-md-5">
                  <div className="p-3 rounded-3" style={{ background: '#ffffff', border: '1px solid #e2e8f0' }}>
                    <div className="fw-bold text-dark mb-2 pb-1 border-bottom" style={{ fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      Customer Billing Breakdown
                    </div>
                    {(() => {
                      const subtotal = Number(orderDetails?.total_price || 0);
                      const platformFee = Number(
                        orderDetails?.platform_fee != null
                          ? orderDetails.platform_fee
                          : ((subtotal * 2) / 100).toFixed(2)
                      );
                      const totalShipping = Number(orderDetails?.shipping_charge || 0);
                      const netShipping = Math.max(0, +(totalShipping - platformFee).toFixed(2));
                      const grandTotal = Number(
                        orderDetails?.overall_price ?? (subtotal + totalShipping)
                      ).toFixed(2);

                      return (
                        <>
                          <div className="d-flex justify-content-between py-1 text-muted" style={{ fontSize: '0.875rem' }}>
                            <span>Subtotal (incl. of taxes):</span>
                            <span className="text-dark fw-medium">₹ {subtotal.toFixed(2)}</span>
                          </div>

                          <div className="d-flex justify-content-between py-1 text-muted" style={{ fontSize: '0.875rem' }}>
                            <span>Platform Fee (2%):</span>
                            <span className="text-dark fw-medium">₹ {platformFee.toFixed(2)}</span>
                          </div>

                          <div className="d-flex justify-content-between py-1 text-muted" style={{ fontSize: '0.875rem' }}>
                            <span>Shipping Charge:</span>
                            <span className="text-dark fw-medium">
                              {netShipping === 0 ? (
                                <span className="text-success fw-bold">FREE</span>
                              ) : (
                                `₹ ${netShipping.toFixed(2)}`
                              )}
                            </span>
                          </div>

                          <div className="d-flex justify-content-between pt-2 mt-1 border-top fw-bold text-dark" style={{ fontSize: '0.95rem' }}>
                            <span>Customer Grand Total:</span>
                            <span className="text-primary">₹ {grandTotal}</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* Net Seller Earnings Summary Card */}
                <div className="col-md-6 col-lg-5">
                  <div className="p-3 rounded-3" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                    <div className="fw-bold mb-2 pb-1 border-bottom d-flex align-items-center justify-content-between" style={{ color: '#166534', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      <span>Net Seller Earnings Summary</span>
                      <FiCheckCircle size={16} />
                    </div>
                    <div className="d-flex justify-content-between py-1 text-muted" style={{ fontSize: '0.875rem' }}>
                      <span>Total Product Price:</span>
                      <span className="text-dark fw-semibold">
                        ₹ {orderDetailsProduct ? orderDetailsProduct.reduce((s, i) => {
                          const pPrice = Number(i.product_price || (i.price * i.quantity) || 0);
                          return s + pPrice;
                        }, 0).toFixed(2) : '0.00'}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between py-1" style={{ color: '#dc2626', fontSize: '0.875rem' }}>
                      <span>Less: Category Commission:</span>
                      <span className="fw-semibold">
                        - ₹ {orderDetailsProduct ? orderDetailsProduct.reduce((s, i) => {
                          const pPrice = Number(i.product_price || (i.price * i.quantity) || 0);
                          const commRate = Number(i.commission_rate || 0);
                          return s + Number(i.commission_amount || (pPrice * (commRate / 100)) || 0);
                        }, 0).toFixed(2) : '0.00'}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between pt-2 mt-1 border-top align-items-center" style={{ borderColor: '#bbf7d0 !important' }}>
                      <span className="fw-bold" style={{ color: '#166534', fontSize: '0.95rem' }}>
                        Total Seller Amount Payable:
                      </span>
                      <span className="fw-bold" style={{ fontSize: '1.3rem', color: '#15803d' }}>
                        ₹ {orderDetailsProduct ? orderDetailsProduct.reduce((s, i) => {
                          const pPrice = Number(i.product_price || (i.price * i.quantity) || 0);
                          const commRate = Number(i.commission_rate || 0);
                          const commAmount = Number(i.commission_amount || (pPrice * (commRate / 100)) || 0);
                          return s + (pPrice - commAmount);
                        }, 0).toFixed(2) : '0.00'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Shiprocket Shipment Details */}
          <div className="order-panel-box mb-4">
            <div className="order-panel-header">
              <div className="order-panel-title">
                <FiTruck size={18} className="text-warning" /> Shiprocket Shipment Details
              </div>
              <button
                className="btn btn-sm"
                style={{
                  background: '#ffffff',
                  border: '1px solid #d1d5db',
                  color: '#374151',
                  borderRadius: '6px',
                  padding: '5px 12px',
                  fontSize: '0.78rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontWeight: '500'
                }}
                onClick={() => {
                  const id = orderDetails?.id || orderDetails?.unique_id;
                  if (id) fetchShiprocketShipments(id);
                }}
                disabled={loadingShipments}
              >
                <FiRefreshCw size={13} className={loadingShipments ? 'fa-spin' : ''} /> 
                {loadingShipments ? 'Refreshing…' : 'Refresh'}
              </button>
            </div>

            <div className="p-3">
              {loadingShipments ? (
                <div className="text-center py-4 text-muted">
                  <FiClock size={20} className="mb-1 d-block mx-auto" />
                  Loading shipment details…
                </div>
              ) : shiprocketShipments.length === 0 ? (
                <div className="text-center py-4">
                  <FiAlertCircle size={28} className="text-muted mb-2" />
                  <p className="text-dark fw-medium mb-1" style={{ fontSize: '0.9rem' }}>No Shiprocket shipment found for this order.</p>
                  <small className="text-muted d-block mb-3">Click below to automatically create and book the shipment with Shiprocket.</small>
                  <button 
                    className="btn btn-sm" 
                    type="button" 
                    onClick={handleShiprocket}
                    style={{
                      background: '#0284c7',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '7px 16px',
                      fontWeight: '500',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '0.85rem'
                    }}
                  >
                    <FiTruck size={15} /> Create Shiprocket Shipment
                  </button>
                </div>
              ) : (
                shiprocketShipments.map((shipment, idx) => (
                  <div
                    key={shipment.id || idx}
                    className="rounded-3 p-3 mb-3"
                    style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}
                  >
                    {/* Header Chips Row */}
                    <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
                      <div className="p-2 px-3 rounded bg-white border">
                        <span className="d-block text-muted" style={{ fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: '600' }}>Shiprocket Order ID</span>
                        <span className="fw-bold text-dark" style={{ fontSize: '0.9rem' }}>#{shipment.shiprocket_order_id || '—'}</span>
                      </div>

                      <div className="p-2 px-3 rounded bg-white border">
                        <span className="d-block text-muted" style={{ fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: '600' }}>Shipment ID</span>
                        <span className="fw-bold text-dark" style={{ fontSize: '0.9rem' }}>#{shipment.shipment_id || '—'}</span>
                      </div>

                      <div className="p-2 px-3 rounded bg-white border">
                        <span className="d-block text-muted" style={{ fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: '600' }}>Order UUID</span>
                        <span className="fw-bold text-dark" style={{ fontSize: '0.9rem' }}>{shipment.frontend_order_uuid || shipment.order_uuid || '—'}</span>
                      </div>

                      <div className="p-2 px-3 rounded bg-white border ms-auto">
                        <span className="d-block text-muted" style={{ fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: '600' }}>Created At</span>
                        <span className="fw-medium text-dark" style={{ fontSize: '0.82rem' }}>
                          {shipment.created_at ? new Date(shipment.created_at).toLocaleString('en-IN') : '—'}
                        </span>
                      </div>
                    </div>

                    {/* Assigned Courier Box */}
                    <div className="p-3 rounded-3" style={{ background: '#ffffff', border: '1px solid #fed7aa' }}>
                      <div className="fw-bold mb-2 d-flex align-items-center gap-2" style={{ color: '#c2410c', fontSize: '0.85rem' }}>
                        <FiTruck size={16} /> Assigned Courier Details
                      </div>
                      <div className="row g-3">
                        <div className="col-6 col-md-3">
                          <span className="d-block text-muted" style={{ fontSize: '0.75rem' }}>Courier Name</span>
                          <span className="fw-semibold text-dark" style={{ fontSize: '0.9rem' }}>
                            {shipment.assigned_courier_name || <span className="text-muted fw-normal">Not assigned</span>}
                          </span>
                        </div>
                        <div className="col-6 col-md-3">
                          <span className="d-block text-muted" style={{ fontSize: '0.75rem' }}>Courier ID</span>
                          <span className="fw-medium text-dark" style={{ fontSize: '0.9rem' }}>
                            {shipment.assigned_courier_id ?? '—'}
                          </span>
                        </div>
                        <div className="col-6 col-md-3">
                          <span className="d-block text-muted" style={{ fontSize: '0.75rem' }}>AWB Code</span>
                          <span style={{ fontFamily: 'monospace', fontSize: '0.95rem', color: '#15803d', fontWeight: '700' }}>
                            {shipment.assigned_awb_code || <span className="text-muted fw-normal" style={{ fontFamily: 'inherit', fontSize: '0.85rem' }}>Not generated</span>}
                          </span>
                        </div>
                        <div className="col-6 col-md-3">
                          <span className="d-block text-muted" style={{ fontSize: '0.75rem' }}>Assigned Freight</span>
                          <span className="fw-bold text-dark" style={{ fontSize: '0.9rem' }}>
                            {shipment.assigned_freight != null ? `₹ ${Number(shipment.assigned_freight).toFixed(2)}` : '—'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Dedicated Tax Invoice / Receipt Component for Printing */}
      <div style={{ position: "absolute", left: "-9999px", top: "-9999px" }}>
        <ComponentToPrint
          ref={componentRef}
          orderData={orderDetails}
          orderDetails={orderDetailsProduct}
        />
      </div>

      {/* Dedicated Shipping Label Component for Printing */}
      <div style={{ position: "absolute", left: "-9999px", top: "-9999px" }}>
        <ComponentToPrintLabel
          ref={printLabelRef}
          orderData={orderDetails}
          orderDetails={orderDetailsProduct}
          shipment={shiprocketShipments?.[0]}
          pickupAddress={addresses?.[0]}
        />
      </div>
    </div>
  );
}

export default OrderSingle;
